import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapLegacySyncRunResult,
  mapLegacySyncTargetList,
  type LegacySyncRunResultRaw,
  type LegacySyncTargetListRaw,
} from "../api/adapters";
import type {
  LegacySyncEndpointPrefix,
  LegacySyncMode,
  LegacySyncOptions,
  LegacySyncRunResult,
  LegacySyncTargetList,
} from "../types";

const LEGACY_SYNC_EXECUTE_START_TIMEOUT_MS = 30_000;
const LEGACY_SYNC_TARGETS_TIMEOUT_MS = 60_000;
const LEGACY_SYNC_RUN_RESULT_TIMEOUT_MS = 60_000;
export const DEFAULT_LEGACY_SYNC_ENDPOINT_PREFIX: LegacySyncEndpointPrefix = "/legacy-sync";

/** 查询旧系统同步对象列表。 */
export async function fetchLegacySyncTargets(
  endpointPrefix: LegacySyncEndpointPrefix = DEFAULT_LEGACY_SYNC_ENDPOINT_PREFIX,
): Promise<LegacySyncTargetList> {
  const response = await httpClient.get<ApiEnvelope<LegacySyncTargetListRaw>>(
    `${endpointPrefix}/targets`,
    { timeout: LEGACY_SYNC_TARGETS_TIMEOUT_MS, params: { include_cursors: true } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("旧系统同步对象接口返回了无效数据");
  }
  return mapLegacySyncTargetList(response.data.data);
}

/** 预览旧系统同步对象。 */
export async function previewLegacySyncTarget(
  targetKey: string,
  syncMode: LegacySyncMode,
  endpointPrefix: LegacySyncEndpointPrefix = DEFAULT_LEGACY_SYNC_ENDPOINT_PREFIX,
  options: LegacySyncOptions = {},
): Promise<LegacySyncRunResult> {
  const response = await httpClient.post<ApiEnvelope<LegacySyncRunResultRaw>>(
    `${endpointPrefix}/targets/${targetKey}/preview`,
    { sync_mode: syncMode, source_updated_from: options.sourceUpdatedFrom,
      max_groups: options.maxGroups ?? 20,
      source_keys: options.sourceKeys ?? [], after_key: options.afterKey ?? null,
      previous_plan_id: options.previousPlanId },
    { timeout: 0 },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("旧系统同步预览接口返回了无效数据");
  }
  return mapLegacySyncRunResult(response.data.data);
}

/** 执行旧系统同步对象。 */
export async function executeLegacySyncTarget(
  targetKey: string,
  syncMode: LegacySyncMode,
  endpointPrefix: LegacySyncEndpointPrefix = DEFAULT_LEGACY_SYNC_ENDPOINT_PREFIX,
  options: LegacySyncOptions = {},
): Promise<LegacySyncRunResult> {
  const response = await httpClient.post<ApiEnvelope<LegacySyncRunResultRaw>>(
    `${endpointPrefix}/targets/${targetKey}/execute`,
    { sync_mode: syncMode, plan_id: options.planId, deadline_at: options.deadlineAt,
      resume_run_id: options.resumeRunId, maintenance_confirmed: options.maintenanceConfirmed },
    { timeout: LEGACY_SYNC_EXECUTE_START_TIMEOUT_MS },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("旧系统同步执行接口返回了无效数据");
  }
  return mapLegacySyncRunResult(response.data.data);
}

/** 请求后台安全收尾，不将请求成功误报为已经暂停。 */
export async function pauseLegacySyncRun(runId: string, endpointPrefix: LegacySyncEndpointPrefix): Promise<LegacySyncRunResult> {
  const response = await httpClient.post<ApiEnvelope<LegacySyncRunResultRaw>>(
    `${endpointPrefix}/runs/${runId}/pause`, {}, { timeout: LEGACY_SYNC_EXECUTE_START_TIMEOUT_MS },
  );
  if (response.data.code !== "OK" || !response.data.data) throw new Error("暂停接口返回无效数据");
  return mapLegacySyncRunResult(response.data.data);
}

/** 查询旧系统同步运行结果。 */
export async function fetchLegacySyncRunResult(
  runId: string,
  endpointPrefix: LegacySyncEndpointPrefix = DEFAULT_LEGACY_SYNC_ENDPOINT_PREFIX,
): Promise<LegacySyncRunResult> {
  const response = await httpClient.get<ApiEnvelope<LegacySyncRunResultRaw>>(
    `${endpointPrefix}/runs/${runId}`,
    { timeout: LEGACY_SYNC_RUN_RESULT_TIMEOUT_MS },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("旧系统同步运行结果接口返回了无效数据");
  }
  return mapLegacySyncRunResult(response.data.data);
}
